// A04 (create variant) — Add new admin user. POST /v1/admin/admin-users.
//
// Password is auto-generated server-side and returned ONCE in the response.
// The creator copies it from the dialog and hands it off to the new admin
// out-of-band; it's never persisted in plaintext (Supabase Auth bcrypts on
// createUser).

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { apiFetch } from "@/lib/api";
import { useToast } from "@/components/Toast";

interface CreateRes {
  ok: boolean;
  id: string;
  generated_password: string | null;
}

export function AdminUserNew() {
  const navigate = useNavigate();
  const { show, ToastEl } = useToast();
  const [form, setForm] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    // Defaults to "Event user" server-side (admin.ts:482) — pre-fill the
    // selector to match that so the creator sees the same default.
    role_label: "Event user" as "Administrator" | "Event user" | "Normal User",
  });
  const [reveal, setReveal] = useState<{ id: string; password: string } | null>(
    null,
  );

  const create = useMutation({
    mutationFn: () =>
      apiFetch<CreateRes>("/v1/admin/admin-users", {
        method: "POST",
        // Phone is sent only when filled (E.164 validation server-side
        // would reject empty string). role_label nests under
        // `permissions` per CreateAdminUserReq's shape — the handler
        // reads it from `body.permissions.role_label`.
        body: {
          username: form.username,
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
          permissions: { role_label: form.role_label },
        },
      }),
    onSuccess: (res) => {
      if (res.generated_password) {
        // Hold the dialog open until the creator confirms — once dismissed
        // the password is gone (server doesn't keep plaintext).
        setReveal({ id: res.id, password: res.generated_password });
      } else {
        show("Admin created", "success");
        navigate(`/app/admin-users/${res.id}`);
      }
    },
    onError: (err) => show(err instanceof Error ? err.message : "Failed", "error"),
  });

  // Phone is optional but if provided must look like an E.164 number
  // (matches the server-side regex in CreateAdminUserReq).
  const phoneOk =
    form.phone.trim().length === 0 || /^\+\d{7,15}$/.test(form.phone.trim());
  const valid =
    Boolean(form.email) &&
    Boolean(form.first_name) &&
    Boolean(form.last_name) &&
    /^[A-Za-z0-9._-]{3,50}$/.test(form.username) &&
    phoneOk;

  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
  };

  const copyPassword = async () => {
    if (!reveal) return;
    try {
      await navigator.clipboard.writeText(reveal.password);
      show("Password copied to clipboard", "success");
    } catch {
      show("Couldn't access clipboard — copy manually", "warning");
    }
  };

  const onContinue = () => {
    if (!reveal) return;
    const id = reveal.id;
    setReveal(null);
    navigate(`/app/admin-users/${id}`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          p: 3,
        }}
      >
        {/* Row 1 — User Name, E-mail, Role (matches Edit page). Status
            (active/inactive) is omitted here because newly-created admins
            are always active; toggle from the Edit page after creation. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          <TextField
            variant="standard"
            fullWidth
            required
            label="User Name"
            helperText="3-50 chars; letters, numbers, . _ -"
            inputProps={{ maxLength: 50 }}
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
          />
          <TextField
            variant="standard"
            fullWidth
            required
            label="E-mail"
            type="email"
            inputProps={{ maxLength: 254 }}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <TextField
            variant="standard"
            fullWidth
            required
            select
            label="Role"
            value={form.role_label}
            onChange={(e) =>
              setForm({
                ...form,
                role_label: e.target.value as typeof form.role_label,
              })
            }
          >
            <MenuItem value="Administrator">Administrator</MenuItem>
            <MenuItem value="Event user">Event User</MenuItem>
            <MenuItem value="Normal User">Normal User</MenuItem>
          </TextField>
          <Box />
        </Box>
        {/* Row 2 — First/Last Name + Phone. Phone optional and editable
            on create (it's locked after that on the Edit page). */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          <TextField
            variant="standard"
            fullWidth
            required
            label="First Name"
            inputProps={{ maxLength: 80 }}
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          />
          <TextField
            variant="standard"
            fullWidth
            required
            label="Last Name"
            inputProps={{ maxLength: 80 }}
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          />
          <TextField
            variant="standard"
            fullWidth
            label="Phone"
            helperText={
              form.phone.trim() && !phoneOk
                ? "Use E.164 format e.g. +85255551234"
                : "Optional · E.164 format e.g. +85255551234"
            }
            error={Boolean(form.phone.trim()) && !phoneOk}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Box>
        <Typography
          sx={{
            mb: 2,
            color: tokens.colors.admin.textBody,
            fontSize: tokens.font.sizes.bodySm,
          }}
        >
          A strong password will be generated automatically and shown to you
          once after the account is created. Copy it and share it securely
          with the new admin — it cannot be retrieved later.
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            disabled={!valid || create.isPending}
            sx={primaryBtn}
            onClick={() => create.mutate()}
          >
            Create
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            onClick={() => navigate("/app/admin-users")}
          >
            Cancel
          </Button>
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(reveal)}
        // Force creator to acknowledge — escape / outside-click would
        // dismiss the only chance to copy the password.
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          onContinue();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>One-time password</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            The new admin's password is shown below. Copy it now and send it
            to them through a secure channel — once you close this dialog
            it cannot be retrieved.
          </Typography>
          <TextField
            fullWidth
            value={reveal?.password ?? ""}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: "monospace" },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copy">
                    <IconButton onClick={copyPassword} aria-label="Copy password">
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" sx={primaryBtn} onClick={onContinue}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
      {ToastEl}
    </Box>
  );
}
